import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import {
  createPipeline,
  deletePipelineStage,
  deletePipelineStageExecution,
  deletePipelineExecution,
  deletePipeline,
  getPipelineStageById,
  getPipelineStageExecutionById,
  getPipelineBySlug,
  queryPipelines,
  restorePipeline,
  updatePipelineStage,
  updatePipelineStageExecution,
  updatePipeline,
} from '@/features/pipelines/data';
import { resolvePipelineSlug } from '@/features/pipelines/utils/pipelineSlug';
import { useProjectFilter } from '@/features/projects';
import {
  gridPaths,
  invalidateGridQuery,
  invalidateMetricsPrefix,
  invalidatePipelineBuildSummaries,
  invalidatePipelineDetail,
  invalidatePipelineDetails,
  invalidatePipelineStageDetail,
  invalidatePipelineStageExecutionDetail,
  invalidatePipelines,
  invalidatePipelineSummaries,
  queryKeys,
  useIsAuthenticated,
  useInvalidateQueries,
} from '@/shared/hooks';
import type { ApiError } from '@/shared/data/client';
import type {
  BuildStatus,
  PipelineDetailDto,
  PipelineListItemDto,
  PipelineProvider,
  PipelineStageExecutionListItemDto,
  PipelineStageListItemDto,
  QueryResult,
} from '@flowqa/shared';

type CreatePipelineInput = {
  projectId: string;
  provider: PipelineProvider;
  pipelineKey?: string | null;
  name: string;
  pipelineUrl?: string | null;
  defaultBranch?: string | null;
  tags?: string[];
};

type UpdatePipelineInput = {
  id: string;
  provider?: PipelineProvider;
  pipelineKey?: string | null;
  name?: string | null;
  pipelineUrl?: string | null;
  defaultBranch?: string | null;
  tags?: string[];
};

type PipelineActionInput = { id: string; projectId?: string | null };
type UpdatePipelineStageInput = {
  id: string;
  stageName: string;
  projectId: string;
};
type PipelineStageActionInput = { id: string; projectId: string };
type UpdatePipelineStageExecutionInput = {
  id: string;
  status?: BuildStatus;
  startedAt?: Date | null;
  finishedAt?: Date | null;
  durationMs?: number | null;
  projectId: string;
};
type PipelineStageExecutionActionInput = { id: string; projectId: string };
type PipelineExecutionActionInput = { id: string; projectId?: string | null };

export const usePipelinesQuery = (
  projectId?: string | null,
  options?: { enabled?: boolean },
): UseQueryResult<QueryResult<PipelineListItemDto>, ApiError> => {
  const isAuthenticated = useIsAuthenticated();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const orgKey = selectedOrganisationId ?? null;
  const effectiveProjectId = projectId ?? selectedProjectId ?? null;
  const isEnabled = isAuthenticated && (options?.enabled ?? true);
  return useQuery<QueryResult<PipelineListItemDto>, ApiError>({
    queryKey: queryKeys.pipelines(effectiveProjectId, orgKey),
    enabled: isEnabled,
    queryFn: async (): Promise<QueryResult<PipelineListItemDto>> =>
      await queryPipelines(effectiveProjectId, selectedOrganisationId ?? undefined, {
        page: 1,
        pageSize: 50,
        sorts: [{ field: 'createdAt', direction: 'desc' }],
        filters: [],
        include: { latestRun: true, metrics: true, metricDeltas: true },
      }),
    staleTime: 30_000,
  });
};

export const usePipelineDetailQuery = (
  slug: string | null,
): UseQueryResult<PipelineDetailDto | null, ApiError> => {
  const isAuthenticated = useIsAuthenticated();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  return useQuery<PipelineDetailDto | null, ApiError>({
    queryKey: queryKeys.pipelineDetail(slug, orgKey),
    enabled: isAuthenticated && Boolean(slug),
    queryFn: async (): Promise<PipelineDetailDto | null> => {
      if (!slug) {
        return null;
      }
      return await getPipelineBySlug(slug, selectedOrganisationId ?? undefined);
    },
    staleTime: 15_000,
  });
};

export const usePipelineStageDetailQuery = (
  id: string | null,
): UseQueryResult<PipelineStageListItemDto | null, ApiError> => {
  const isAuthenticated = useIsAuthenticated();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  return useQuery<PipelineStageListItemDto | null, ApiError>({
    queryKey: queryKeys.pipelineStageDetail(id, orgKey),
    enabled: isAuthenticated && Boolean(id),
    queryFn: async (): Promise<PipelineStageListItemDto | null> => {
      if (!id) {
        return null;
      }
      return await getPipelineStageById(id, selectedOrganisationId ?? undefined);
    },
    staleTime: 15_000,
  });
};

export const usePipelineStageExecutionDetailQuery = (
  id: string | null,
): UseQueryResult<PipelineStageExecutionListItemDto | null, ApiError> => {
  const isAuthenticated = useIsAuthenticated();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  return useQuery<PipelineStageExecutionListItemDto | null, ApiError>({
    queryKey: queryKeys.pipelineStageExecutionDetail(id, orgKey),
    enabled: isAuthenticated && Boolean(id),
    queryFn: async (): Promise<PipelineStageExecutionListItemDto | null> => {
      if (!id) {
        return null;
      }
      return await getPipelineStageExecutionById(id, selectedOrganisationId ?? undefined);
    },
    staleTime: 15_000,
  });
};

export const useCreatePipeline = (): UseMutationResult<
  PipelineDetailDto,
  ApiError,
  CreatePipelineInput
> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;

  return useMutation<PipelineDetailDto, ApiError, CreatePipelineInput>({
    mutationFn: async (input) => await createPipeline(input, selectedOrganisationId),
    onSuccess: async (created) => {
      const pipelineSlug = resolvePipelineSlug(created);
      queryClient.setQueryData(queryKeys.pipelineDetail(pipelineSlug, orgKey), created);
      await invalidateQueries([
        invalidatePipelines(undefined, orgKey),
        invalidatePipelines(created.projectId, orgKey),
        invalidateGridQuery(gridPaths.pipelines),
      ]);
    },
  });
};

export const useUpdatePipeline = (): UseMutationResult<
  PipelineDetailDto,
  ApiError,
  UpdatePipelineInput
> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;

  return useMutation<PipelineDetailDto, ApiError, UpdatePipelineInput>({
    mutationFn: async (input) => await updatePipeline(input, selectedOrganisationId),
    onSuccess: async (updated) => {
      const pipelineSlug = resolvePipelineSlug(updated);
      queryClient.setQueryData(queryKeys.pipelineDetail(pipelineSlug, orgKey), updated);
      await invalidateQueries([
        invalidatePipelines(undefined, orgKey),
        invalidatePipelines(updated.projectId, orgKey),
        invalidatePipelineDetail(pipelineSlug, orgKey),
        invalidateGridQuery(gridPaths.pipelines),
        invalidateGridQuery('pipelines-daily-history'),
        invalidatePipelineSummaries(),
        invalidatePipelineBuildSummaries(),
        invalidateMetricsPrefix(),
      ]);
    },
  });
};

export const useDeletePipeline = (): UseMutationResult<
  { id: string; projectId?: string | null },
  ApiError,
  PipelineActionInput
> => {
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;

  return useMutation<{ id: string; projectId?: string | null }, ApiError, PipelineActionInput>({
    mutationFn: async (input) => {
      const result = await deletePipeline({ id: input.id }, selectedOrganisationId);
      return { ...result, projectId: input.projectId };
    },
    onSuccess: async (deleted) => {
      await invalidateQueries([
        invalidatePipelines(undefined, orgKey),
        ...(deleted.projectId ? [invalidatePipelines(deleted.projectId, orgKey)] : []),
        invalidatePipelineDetails(orgKey),
        invalidateGridQuery(gridPaths.pipelines),
        invalidateGridQuery('pipelines-daily-history'),
        invalidatePipelineSummaries(),
        invalidatePipelineBuildSummaries(),
        invalidateMetricsPrefix(),
      ]);
    },
  });
};

export const useRestorePipeline = (): UseMutationResult<
  { id: string; projectId?: string | null },
  ApiError,
  PipelineActionInput
> => {
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;

  return useMutation<{ id: string; projectId?: string | null }, ApiError, PipelineActionInput>({
    mutationFn: async (input) => {
      const result = await restorePipeline({ id: input.id }, selectedOrganisationId);
      return { ...result, projectId: input.projectId };
    },
    onSuccess: async (restored) => {
      await invalidateQueries([
        invalidatePipelines(undefined, orgKey),
        ...(restored.projectId ? [invalidatePipelines(restored.projectId, orgKey)] : []),
        invalidatePipelineDetails(orgKey),
        invalidateGridQuery(gridPaths.pipelines),
        invalidateGridQuery('pipelines-daily-history'),
        invalidatePipelineSummaries(),
        invalidatePipelineBuildSummaries(),
        invalidateMetricsPrefix(),
      ]);
    },
  });
};

export const useDeletePipelineExecution = (): UseMutationResult<
  { id: string; projectId?: string | null },
  ApiError,
  PipelineExecutionActionInput
> => {
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;

  return useMutation<
    { id: string; projectId?: string | null },
    ApiError,
    PipelineExecutionActionInput
  >({
    mutationFn: async (input) => {
      const result = await deletePipelineExecution({ id: input.id }, selectedOrganisationId);
      return { ...result, projectId: input.projectId };
    },
    onSuccess: async (deleted) => {
      await invalidateQueries([
        invalidatePipelines(undefined, orgKey),
        ...(deleted.projectId ? [invalidatePipelines(deleted.projectId, orgKey)] : []),
        invalidatePipelineDetails(orgKey),
        invalidateGridQuery(gridPaths.pipelines),
        invalidateGridQuery('pipelines-daily-history'),
        invalidatePipelineSummaries(),
        invalidatePipelineBuildSummaries(),
        invalidateMetricsPrefix(),
      ]);
    },
  });
};

export const useUpdatePipelineStage = (): UseMutationResult<
  PipelineStageListItemDto,
  ApiError,
  UpdatePipelineStageInput
> => {
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;

  return useMutation<PipelineStageListItemDto, ApiError, UpdatePipelineStageInput>({
    mutationFn: async (input) => await updatePipelineStage(input, selectedOrganisationId),
    onSuccess: async (updated) => {
      await invalidateQueries([
        invalidatePipelines(undefined, orgKey),
        invalidatePipelines(updated.projectId, orgKey),
        invalidateGridQuery(gridPaths.pipelines),
        invalidateGridQuery('stages/query'),
        invalidateGridQuery('stage-runs/query'),
        invalidatePipelineStageDetail(updated.id, orgKey),
        invalidatePipelineSummaries(),
        invalidatePipelineBuildSummaries(),
        invalidateMetricsPrefix(),
      ]);
    },
  });
};

export const useDeletePipelineStage = (): UseMutationResult<
  { id: string; projectId: string },
  ApiError,
  PipelineStageActionInput
> => {
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;

  return useMutation<{ id: string; projectId: string }, ApiError, PipelineStageActionInput>({
    mutationFn: async (input) => {
      const result = await deletePipelineStage(input, selectedOrganisationId);
      return { ...result, projectId: input.projectId };
    },
    onSuccess: async (deleted) => {
      await invalidateQueries([
        invalidatePipelines(undefined, orgKey),
        invalidatePipelines(deleted.projectId, orgKey),
        invalidateGridQuery(gridPaths.pipelines),
        invalidateGridQuery('stages/query'),
        invalidateGridQuery('stage-runs/query'),
        invalidatePipelineStageDetail(deleted.id, orgKey),
        invalidatePipelineSummaries(),
        invalidatePipelineBuildSummaries(),
        invalidateMetricsPrefix(),
      ]);
    },
  });
};

export const useUpdatePipelineStageExecution = (): UseMutationResult<
  PipelineStageExecutionListItemDto,
  ApiError,
  UpdatePipelineStageExecutionInput
> => {
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;

  return useMutation<
    PipelineStageExecutionListItemDto,
    ApiError,
    UpdatePipelineStageExecutionInput
  >({
    mutationFn: async (input) => await updatePipelineStageExecution(input, selectedOrganisationId),
    onSuccess: async (updated) => {
      await invalidateQueries([
        invalidatePipelines(undefined, orgKey),
        invalidatePipelines(updated.projectId, orgKey),
        invalidateGridQuery(gridPaths.pipelines),
        invalidateGridQuery('stages/query'),
        invalidateGridQuery('stage-runs/query'),
        invalidateGridQuery('pipelines-daily-history'),
        invalidatePipelineStageExecutionDetail(updated.id, orgKey),
        invalidatePipelineSummaries(),
        invalidatePipelineBuildSummaries(),
        invalidateMetricsPrefix(),
      ]);
    },
  });
};

export const useDeletePipelineStageExecution = (): UseMutationResult<
  { id: string; projectId: string },
  ApiError,
  PipelineStageExecutionActionInput
> => {
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;

  return useMutation<
    { id: string; projectId: string },
    ApiError,
    PipelineStageExecutionActionInput
  >({
    mutationFn: async (input) => {
      const result = await deletePipelineStageExecution(input, selectedOrganisationId);
      return { ...result, projectId: input.projectId };
    },
    onSuccess: async (deleted) => {
      await invalidateQueries([
        invalidatePipelines(undefined, orgKey),
        invalidatePipelines(deleted.projectId, orgKey),
        invalidateGridQuery(gridPaths.pipelines),
        invalidateGridQuery('stages/query'),
        invalidateGridQuery('stage-runs/query'),
        invalidateGridQuery('pipelines-daily-history'),
        invalidatePipelineStageExecutionDetail(deleted.id, orgKey),
        invalidatePipelineSummaries(),
        invalidatePipelineBuildSummaries(),
        invalidateMetricsPrefix(),
      ]);
    },
  });
};
